import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import NearbyDrivers from "../components/NearbyDrivers";
import { fmtMoney, fmtDateTime, STATUS_LABEL, EQUIPMENT_LABEL } from "../lib/format";
import { CloudSun, MapPin, FileUp, ChevronRight, CheckCircle2, AlertTriangle, Cloud, Trash2, Clock } from "lucide-react";

const STATUS_FLOW = ["draft", "published", "assigned", "started", "picked_up", "in_transit", "delivered", "closed"];

function WeatherCard({ label, w }) {
  if (!w) return null;
  const icon = w.risk === "high" ? <AlertTriangle size={16} /> : w.risk === "medium" ? <Cloud size={16} /> : <CloudSun size={16} />;
  return (
    <div className="card-base p-4" data-testid={`weather-${label.toLowerCase().includes("pickup") ? "pickup" : "dropoff"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">{label}</div>
      <div className="font-display text-2xl font-bold text-navy">{w.city}</div>
      <div className="text-sm text-slate-600 my-2">
        {w.temp_f != null ? `${w.condition} · ${w.temp_f}°F · Wind ${w.wind_mph} mph` : w.condition}
      </div>
      {w.timezone && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2" data-testid="weather-localtime">
          <Clock size={13} className="text-gold" />
          <span className="font-semibold text-navy">{w.local_time}</span> local · {w.timezone} (UTC{w.utc_offset_hours >= 0 ? "+" : ""}{w.utc_offset_hours})
        </div>
      )}
      <span className={`badge ${w.risk}`}>{icon}<span className="ml-1">Risk: {w.risk}</span></span>
    </div>
  );
}

export default function LoadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [load, setLoad] = useState(null);
  const [bids, setBids] = useState([]);
  const [weather, setWeather] = useState(null);
  const [docs, setDocs] = useState([]);
  const [bidAmt, setBidAmt] = useState("");
  const [bidNotes, setBidNotes] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [l, b, w, d] = await Promise.all([
      api.get(`/loads/${id}`),
      api.get(`/bids`, { params: { load_id: id } }),
      api.get(`/loads/${id}/weather`).catch(() => ({ data: null })),
      api.get(`/loads/${id}/documents`).catch(() => ({ data: [] })),
    ]);
    setLoad(l.data); setBids(b.data); setWeather(w.data); setDocs(d.data);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  const placeBid = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/bids", { load_id: id, amount: Number(bidAmt), notes: bidNotes });
      setBidAmt(""); setBidNotes("");
      await refresh();
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const decideBid = async (bid_id, decision) => {
    await api.post(`/bids/${bid_id}/decision`, { decision });
    await refresh();
  };

  const updateStatus = async (status) => {
    await api.patch(`/loads/${id}`, { status });
    await refresh();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("type", e.target.dataset.type);
    await api.post(`/loads/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    await refresh();
    e.target.value = "";
  };

  const viewDoc = async (docId) => {
    const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${docId}`);
    await refresh();
  };

  if (!load) return <div className="text-slate-500">Loading…</div>;

  const isShipperOwner = (user.role === "shipper" || user.role === "ops_admin" || user.role === "super_admin")
    && (user.role !== "shipper" || load.company_id === user.company_id);
  const isCarrier = user.role === "carrier_owner" || user.role === "dispatcher";
  const isDriver = user.role === "driver" && load.assigned_driver_id === user.user_id;
  const isAssignedCarrier = (user.role === "carrier_owner" || user.role === "dispatcher") && load.assigned_carrier_id === user.company_id;
  const canManageDocs = isShipperOwner || isDriver || isAssignedCarrier;
  const canBid = isCarrier && load.status === "published" && !load.assigned_carrier_id;
  const currentStepIdx = STATUS_FLOW.indexOf(load.status);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-1">Load · <span className="font-mono">{load.load_id}</span></div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">
            {load.pickup_city}, {load.pickup_state} → {load.dropoff_city}, {load.dropoff_state}
          </h1>
          <div className="text-slate-500 mt-1">
            {EQUIPMENT_LABEL[load.equipment_type]} · {load.weight?.toLocaleString()} lbs · {load.distance} mi
          </div>
        </div>
        <span className={`badge ${load.status} text-sm px-3 py-1`}>{STATUS_LABEL[load.status]}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Status timeline */}
          <div className="card-base p-6">
            <div className="font-display text-lg font-bold text-navy mb-4">Status Timeline</div>
            <div className="flex flex-wrap items-center gap-2">
              {STATUS_FLOW.map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold ${i <= currentStepIdx ? "bg-gold text-navy" : "bg-slate-100 text-slate-400"}`}>
                    {i <= currentStepIdx ? <CheckCircle2 size={14} /> : <span className="w-3.5 h-3.5 rounded-full border border-slate-400" />}
                    {STATUS_LABEL[s]}
                  </div>
                  {i < STATUS_FLOW.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
                </React.Fragment>
              ))}
            </div>

            {(isShipperOwner || isDriver) && load.status !== "delivered" && load.status !== "closed" && (
              <div className="mt-5 flex flex-wrap gap-2">
                {isShipperOwner && load.status === "draft" && <button onClick={() => updateStatus("published")} className="btn-primary text-xs" data-testid="status-publish">Publish</button>}
                {isDriver && load.status === "assigned" && <button onClick={() => updateStatus("started")} className="btn-primary text-xs" data-testid="status-started">Mark started</button>}
                {isDriver && load.status === "started" && <button onClick={() => updateStatus("picked_up")} className="btn-primary text-xs" data-testid="status-picked-up">Mark picked up</button>}
                {isDriver && load.status === "picked_up" && <button onClick={() => updateStatus("in_transit")} className="btn-primary text-xs" data-testid="status-in-transit">Mark in transit</button>}
                {isDriver && load.status === "in_transit" && <button onClick={() => updateStatus("delivered")} className="btn-primary text-xs" data-testid="status-delivered">Mark delivered</button>}
                {isShipperOwner && load.status === "delivered" && <button onClick={() => updateStatus("closed")} className="btn-secondary text-xs" data-testid="status-closed">Close load</button>}
              </div>
            )}
          </div>

          {/* Map placeholder */}
          <div className="card-base p-6">
            <div className="font-display text-lg font-bold text-navy mb-3">Route</div>
            <div className="aspect-[16/7] rounded-md bg-navy text-white flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #ffb400 0, transparent 60%), radial-gradient(circle at 80% 50%, #ffb400 0, transparent 60%)" }} />
              <div className="relative grid grid-cols-3 items-center gap-4 w-full max-w-lg px-6">
                <div className="text-center">
                  <MapPin className="mx-auto text-gold mb-2" />
                  <div className="font-display font-bold">{load.pickup_city}</div>
                  <div className="text-xs text-slate-300">{load.pickup_state} {load.pickup_zip}</div>
                </div>
                <div className="text-center">
                  <div className="h-px bg-gold/60 relative">
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] font-bold text-gold">{load.distance} mi</div>
                  </div>
                </div>
                <div className="text-center">
                  <MapPin className="mx-auto text-gold mb-2" />
                  <div className="font-display font-bold">{load.dropoff_city}</div>
                  <div className="text-xs text-slate-300">{load.dropoff_state} {load.dropoff_zip}</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
              <div><div className="label-base">Pickup time</div><div className="font-mono font-semibold">{fmtDateTime(load.pickup_time)}</div></div>
              <div><div className="label-base">Delivery time</div><div className="font-mono font-semibold">{fmtDateTime(load.delivery_time)}</div></div>
            </div>
          </div>

          {/* Bids */}
          <div className="card-base">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display text-lg font-bold text-navy">Bids ({bids.length})</div>
              <div className="text-xs text-slate-500">Listed: <span className="font-mono font-bold text-navy">{fmtMoney(load.rate)}</span></div>
            </div>
            {canBid && (
              <form onSubmit={placeBid} className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 grid md:grid-cols-4 gap-3" data-testid="place-bid-form">
                <input type="number" step="0.01" placeholder="Your bid (USD)" value={bidAmt} onChange={e => setBidAmt(e.target.value)} className="input-base" required data-testid="bid-amount" />
                <input placeholder="Notes (optional)" value={bidNotes} onChange={e => setBidNotes(e.target.value)} className="input-base md:col-span-2" data-testid="bid-notes" />
                <button disabled={busy} className="btn-primary" data-testid="bid-submit">Place bid</button>
                {err && <div className="text-red-600 text-xs md:col-span-4">{err}</div>}
              </form>
            )}
            <div className="overflow-x-auto">
              <table className="table-base">
                <thead><tr><th>Carrier</th><th>Amount</th><th>Notes</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {bids.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-8">No bids yet.</td></tr>}
                  {bids.map(b => (
                    <tr key={b.bid_id} data-testid={`bid-row-${b.bid_id}`}>
                      <td className="font-mono text-xs">{b.carrier_company_id}</td>
                      <td className="font-mono font-bold text-navy">{fmtMoney(b.amount)}</td>
                      <td className="text-sm text-slate-600">{b.notes || "—"}</td>
                      <td><span className={`badge ${b.status}`}>{b.status}</span></td>
                      <td>
                        {isShipperOwner && b.status === "pending" && (
                          <div className="flex gap-2">
                            <button onClick={() => decideBid(b.bid_id, "accept")} className="btn-primary text-xs px-3 py-1" data-testid={`accept-${b.bid_id}`}>Accept</button>
                            <button onClick={() => decideBid(b.bid_id, "reject")} className="btn-outline text-xs px-3 py-1" data-testid={`reject-${b.bid_id}`}>Reject</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          {/* Weather */}
          <div className="space-y-3">
            <WeatherCard label="Pickup Weather" w={weather?.pickup} />
            <WeatherCard label="Dropoff Weather" w={weather?.dropoff} />
          </div>

          {/* Nearby drivers (shipper/carrier/admin) */}
          {(isShipperOwner || isCarrier) && load.status === "published" && (
            <NearbyDrivers loadId={load.load_id} />
          )}

          {/* Documents */}
          {canManageDocs && (
          <div className="card-base p-6">
            <div className="font-display text-lg font-bold text-navy mb-4">Documents</div>
            <div className="space-y-2 mb-4">
              {docs.length === 0 && <div className="text-sm text-slate-400">No documents uploaded.</div>}
              {docs.map(d => (
                <div key={d.document_id} className="flex items-center justify-between text-sm border border-slate-100 rounded-md p-2" data-testid={`doc-${d.document_id}`}>
                  <div>
                    <div className="font-semibold text-slate-900">{d.filename}</div>
                    <div className="text-xs text-slate-500 uppercase">{d.type} · {(d.size/1024).toFixed(1)} KB</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => viewDoc(d.document_id)} className="text-gold font-semibold text-xs px-2 py-1 hover:underline" data-testid={`view-doc-${d.document_id}`}>View</button>
                    {(d.uploaded_by === user.user_id || isShipperOwner) && (
                      <button onClick={() => removeDoc(d.document_id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`delete-doc-${d.document_id}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["bol", "pod", "rate_confirmation", "other"].map(t => (
                <label key={t} className="btn-outline text-xs cursor-pointer" data-testid={`upload-${t}`}>
                  <FileUp size={14} /> {t.toUpperCase()}
                  <input type="file" data-type={t} className="hidden" onChange={upload} accept=".pdf,.jpg,.jpeg,.png" />
                </label>
              ))}
            </div>
          </div>
          )}

          {/* Broker margin (admin only) */}
          {load.broker_margin != null && (
            <div className="card-base p-6 border-gold/40">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gold mb-2">Broker margin</div>
              <div className="font-display text-3xl font-bold text-navy">{fmtMoney(load.broker_margin)}</div>
              <div className="text-xs text-slate-500 mt-1">Admin visibility only</div>
            </div>
          )}

          {/* Notes */}
          {load.notes && (
            <div className="card-base p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Notes</div>
              <div className="text-sm text-slate-700 whitespace-pre-line">{load.notes}</div>
            </div>
          )}
        </aside>
      </div>

      <div><Link to="/app/loads" className="text-sm text-gold font-semibold" data-testid="back-to-loads">← Back to loads</Link></div>
    </div>
  );
}
